import { LOGO_API_KEY } from "../../constants/enviroment";
import { API_ENDPOINTS } from "../../constants/api";
import { extractDomain } from "./extractDomain";
import { CompetitorWithLogo } from "../../types/competitor";
import { CompetitorsResponse } from "../auth/types";
import { LogoDescribeResponse } from "../brands/types";
import { apiGet } from "../../utils/apiClient";

type CompetitorFromAPI = CompetitorsResponse["competitors"][number];

export type CompetitorLogoAndDescription = Pick<
  CompetitorWithLogo,
  "logoUrl" | "website" | "description"
>;

export async function fetchLogoDescribeByDomain(
  domain: string
): Promise<Pick<CompetitorWithLogo, "logoUrl" | "description">> {
  if (!domain || !LOGO_API_KEY) {
    return {};
  }

  try {
    const data = await apiGet<LogoDescribeResponse>(API_ENDPOINTS.logoDescribe(domain), {
      auth: "logoApi",
    });

    return {
      logoUrl: data.logo,
      description: data.description ?? "",
    };
  } catch {
    return {};
  }
}

export async function fetchCompetitorLogosBatch(
  competitors: CompetitorFromAPI[]
): Promise<Map<string, CompetitorLogoAndDescription>> {
  const result = new Map<string, CompetitorLogoAndDescription>();

  if (!competitors?.length) {
    return result;
  }

  await Promise.all(
    competitors.map(async (competitor) => {
      if (!competitor.url) {
        return;
      }

      const domain = extractDomain(competitor.url);
      if (!domain) {
        return;
      }

      const { logoUrl, description } = await fetchLogoDescribeByDomain(domain);
      
      result.set(competitor.name, {
        logoUrl,
        website: domain,
        description: description ?? "",
      });
    })
  );

  return result;
}

export async function resolveCompetitorDescription(
  descriptionFromPayload?: string,
  website?: string,
): Promise<string> {
  let description = descriptionFromPayload ?? "";

  if (!description && website) {
    const described = await fetchLogoDescribeByDomain(website);
    description = described.description ?? "";
  }

  return description;
}

export function mapCompetitorsWithLogoAndDescription(
  competitors: CompetitorFromAPI[],
  logosMap: Map<string, CompetitorLogoAndDescription>
): CompetitorWithLogo[] {
  return competitors.map((item) => {
    const fromLogo = logosMap.get(item.name);

    return {
      name: item.name,
      logoUrl: fromLogo?.logoUrl,
      website: fromLogo?.website,
      description: item.description ?? fromLogo?.description ?? "",
    };
  });
}
